import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Mail, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/* TeaserOverlay
 * Wraps a RoyAI section in a permanent gold "VIP Beta Feature" veil that
 * blocks all interaction with the underlying UI while still showing it as
 * a preview. Includes an early-access email capture that hits the shared
 * waitlist endpoint. */
export default function TeaserOverlay({ children, featureKey, label = "VIP Beta Feature: Coming August 2026" }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await api.post("/waitlist/early-access", { email: trimmed, feature: featureKey });
      setDone(true);
      toast.success("You're on the early-access list — we'll email you at launch.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" data-testid={`teaser-overlay-${featureKey}`}>
      {/* Underlying content — visible, blurred, non-interactive */}
      <div
        aria-hidden="true"
        className="pointer-events-none select-none opacity-50 blur-[1.5px]"
        inert=""
      >
        {children}
      </div>

      {/* Gold veil */}
      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/[0.08] via-black/60 to-black/80 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-black/85 border border-amber-500/40 shadow-[0_0_60px_-15px_rgba(212,175,55,0.5)] p-6 text-center backdrop-blur-sm">
          <div className="inline-flex items-center gap-2 mb-3 text-amber-400 text-[10px] uppercase tracking-widest font-mono-tech border border-amber-500/40 px-2.5 py-1">
            <Sparkles size={11} /> {label}
          </div>
          <p className="text-sm text-neutral-300 mb-4">
            This module is unlocking for VIP Beta members first. Drop your
            email and we&apos;ll send you access the moment it opens.
          </p>
          {done ? (
            <div className="bg-emerald-500/10 border border-emerald-500/40 px-4 py-3 text-xs text-emerald-300">
              ✓ You&apos;re on the list. We&apos;ll reach out at launch.
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2">
              <div className="flex items-center gap-2 flex-1 bg-[#050505] border border-white/10 px-3">
                <Mail size={12} className="text-amber-500 shrink-0" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.slice(0, 200))}
                  placeholder="you@studio.com"
                  data-testid={`teaser-email-${featureKey}`}
                  className="w-full bg-transparent py-2 text-xs text-white outline-none placeholder:text-neutral-700"
                />
              </div>
              <button
                type="submit"
                disabled={busy || !email.trim()}
                data-testid={`teaser-submit-${featureKey}`}
                className="inline-flex items-center justify-center gap-1.5 bg-amber-500 text-black font-semibold px-4 py-2 text-xs uppercase tracking-widest hover:brightness-110 transition-all disabled:opacity-40"
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : "Notify Me"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
